import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { AlertService } from "../../alert/services/alert.service";
import { HybridTTSService } from "../../../core/services/hybrid-tts.service";
import { DatadogService } from "../../../core/observability/datadog.service";

@Injectable({
  providedIn: "root",
})
export class DonationAudioService {
  constructor(
    private hybridTTSService: HybridTTSService,
    private alertService: AlertService,
    private datadogService: DatadogService
  ) {}

  /**
   * Decide e executa o áudio (replay ou TTS) para um evento de donation.
   * Mantém a regra: usar audioUrl se disponível; caso contrário, gerar TTS.
   */
  async play(event: any, streamerId: string, fallbackTTS: () => Promise<void>): Promise<void> {
    const settings = this.alertService.getSettings(event.type);
    const donationId = event.data.id;

    console.log('🔍 [DonationAudio] play() chamado', { donationId, isTest: donationId?.startsWith("test-") });

    // Para testes, usar apenas TTS nativo do browser (não gera TTS com IA)
    if (donationId?.startsWith("test-")) {
      console.log('✅ [DonationAudio] É TESTE! Chamando fallbackTTS()');
      await fallbackTTS();
      return;
    }

    const hasReplay = !!event.data.audioUrl;
    const isVoiceRecording = event.data.audioType === "ORIGINAL";

    if (hasReplay) {
      await this.playReplay(event, streamerId, settings, fallbackTTS);
      return;
    }

    await this.playRealtimeTTS(event, settings, fallbackTTS);
  }

  private async playReplay(
    event: any,
    streamerId: string,
    settings: any,
    fallbackTTS: () => Promise<void>
  ): Promise<void> {
    const isVoiceRecording = event.data.audioType === "ORIGINAL";
    const donationId = event.data.id;
    const originalUrl = event.data.audioUrl;

    // 📱 Processar URL para Moblin/iOS se necessário
    const audioType = isVoiceRecording ? 'voice_recording' : 'tts';
    const audioUrl = this.hybridTTSService.processAudioUrl(
      originalUrl,
      donationId,
      audioType,
      streamerId
    );
    
    console.log(`🔊 [DonationAudio] REPLAY | DonationId: ${donationId} | Type: ${audioType} | VoiceRec: ${isVoiceRecording}`);
    console.log(`   📍 URL changed: ${originalUrl !== audioUrl ? '✅ YES (gain will be applied)' : '❌ NO (original audio)'}`);
    
    console.log(`🔊 [DonationAudio] REPLAY | DonationId: ${donationId} | Type: ${audioType} | VoiceRec: ${isVoiceRecording}`);
    console.log(`   📍 URL changed: ${originalUrl !== audioUrl ? '✅ YES (gain will be applied)' : '❌ NO (original audio)'}`);

    try {
      await firstValueFrom(
        this.hybridTTSService.playAIAudio(
          audioUrl,
          settings,
          isVoiceRecording
        )
      );
      this.datadogService.trackAction("donation_audio_replay_success", {
        eventId: event.id,
        type: event.type,
        donationId: event.data.id,
        isVoiceRecording,
      });
    } catch (error: any) {
      this.datadogService.trackAction("donation_audio_replay_error", {
        eventId: event.id,
        type: event.type,
        donationId: event.data.id,
        isVoiceRecording,
        error: String(error),
      });
      
      // 🔄 Se receber erro 410 (history expirado), tentar regenerar automaticamente
      if (error?.status === 410 || error?.error?.error === "HISTORY_EXPIRED") {
        console.warn("⚠️ Áudio expirado detectado. Tentando regenerar...", error);
        try {
          // Chamar regeneração em tempo real (como se não tivesse cache)
          await this.playRealtimeTTS(event, settings, fallbackTTS);
          return;
        } catch (regenError) {
          console.error("❌ Falha na regeneração automática:", regenError);
          await fallbackTTS();
          return;
        }
      }
      
      console.error("❌ Erro ao reproduzir áudio do replay:", error);
      await fallbackTTS();
    }
  }

  private async playRealtimeTTS(
    event: any,
    settings: any,
    fallbackTTS: () => Promise<void>
  ): Promise<void> {
    const donationId = event.data.id;
    const streamerId = event.data.streamerId;

    try {
      const ttsResponse = await this.hybridTTSService.generateDonationTTS(
        donationId,
        {
          includeDonor: true,
          includeAmount: true,
          fallbackVoice: settings.speechVoice,
        }
      );

      if (ttsResponse.success) {
        await this.hybridTTSService.playTTSResponse(ttsResponse, settings, donationId, streamerId);
        this.datadogService.trackAction("donation_audio_tts_success", {
          eventId: event.id,
          type: event.type,
          donationId,
          voiceUsed: ttsResponse.voiceUsed,
        });
      } else {
        this.datadogService.trackAction("donation_audio_tts_failed", {
          eventId: event.id,
          type: event.type,
          donationId,
          error: ttsResponse.error,
        });
        console.warn("TTS híbrido falhou, usando fallback:", ttsResponse.error);
        await fallbackTTS();
      }
    } catch (error) {
      this.datadogService.trackAction("donation_audio_tts_failed", {
        eventId: event.id,
        type: event.type,
        donationId,
        error: String(error),
      });
      console.error("Erro no TTS híbrido:", error);
      await fallbackTTS();
    }
  }
}
